import React from 'react';
import {
    Typography,
    Box,
    Paper,
    Grid,
    Button,
} from '@mui/material';

import RiskScoreDisplay from '../RiskScoreDisplay';
import BusinessHeader from './BusinessHeader';
import BaseDetailCard from '../BaseDetailCard';
import RefreshIcon from '@mui/icons-material/Refresh';
import { typography, colors } from '../styles/globals';
import TexturePattern from '../header_texture.svg';

const BusinessSummarySection = ({
    request
}) => {
    const name = request.business_name;
    const address = request.address;
    const sourcesScanned = request.sources_scanned;

    // Dummy data
    const profileCompleteness = Math.min(Math.round(request.profile_completeness * 100), 100);
    const riskScore = Math.round(request.score * 100);

    // Format date to HH:MM AM/PM, Month DD, YYYY
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const hasTimezone = dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString);
        const normalized = hasTimezone ? dateString : dateString + 'Z';
        const date = new Date(normalized);
        const time = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
        const datePart = date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
        return `${time}, ${datePart}`;
    };

    const getGaugeSegmentColor = (riskScore) => {
        if (riskScore <= 30) return 'rgba(16, 185, 129, 0.35)';  // Green
        if (riskScore <= 60) return 'rgba(224, 160, 87, 0.35)';   // Orange
        return 'rgba(253, 87, 69, 0.35)';                       // Red
    }

    return (
        <Paper
            elevation={3}
            sx={{
                px: 3,
                py: 2,
                borderRadius: 2,
                position: 'relative',
                overflow: 'hidden',
                background: `
                    radial-gradient(
                        circle at 75% 25%,
                        ${getGaugeSegmentColor(riskScore)} 0%,
                        rgba(23, 36, 85, 0) 40%
                    ),
                    #172455
                `,
                maxHeight: '240px',
                height: 'auto',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: `url(${TexturePattern})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    opacity: 0.3,
                    pointerEvents: 'none',
                    zIndex: 1
                }
            }}
        >
            <Grid container spacing={2} sx={{
                height: '100%',
                alignItems: 'stretch',
                position: 'relative',
                zIndex: 2
            }}>
                <Grid item xs={12} md={6} sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: "flex-start",
                    gap: 1.5
                }}>
                    <BusinessHeader 
                        name={name} 
                        address={address} 
                    />
                    <Box sx={{ display: 'flex', gap: 1, pt: 2, mt: 1 }}>
                        {/* Profile completeness */}
                        <BaseDetailCard
                            title="Profile complete"
                            showProgress={true}
                            progressValue={profileCompleteness}
                            bottomLeftContent={`${profileCompleteness}%`}
                        />
                        <BaseDetailCard
                            title="Sources scanned"
                            showProgress={false}
                            bottomLeftContent={`${sourcesScanned}`}
                        />
                    </Box>

                </Grid>
                <Grid item xs={12} md={6} sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: { xs: "center", md: "flex-end" },
                    alignItems: { xs: "center", md: "flex-end" },
                    height: '100%',
                    gap: { xs: 1, md: 0.5 },


                }}>
                    <Typography sx={{
                        fontWeight: typography.fontWeights.regular,
                        color: colors.lightGrey,
                        fontSize: typography.fontSizes.small,
                        whiteSpace: 'nowrap',
                        alignSelf: "flex-end",
                        mb: 0.5,
                        mr: 2
                    }}>
                        {formatDate(request.created_at)}
                    </Typography>
                    <Box>
                        <RiskScoreDisplay score={riskScore} />
                    </Box>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default BusinessSummarySection;